import apiService from "@/core/services/apiService";
import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import { Mutations } from "@/store/enums/store.enums";
import { NewsletterActions, NewsletterMutation } from "@/modules/newsletter/store/enums";

@Module
export default class Store extends VuexModule {
  renderKey = 1;
  listParams = {
    page: 1,
    results: 10,
    search: null,
    sort: [],
    filter: {}
  };
  listCache = [];
  listTotal = 0;
  settings = {};
  snippetsCache = [];
  snippetsTotal = 0;

  get getNewsletterRenderKey() {
    return "newsletter-" + this.renderKey;
  }

  get getNewsletterLastListResult() {
    return this.listCache;
  }

  get getNewsletterLastListTotal() {
    return this.listTotal;
  }

  get getNewsletterSettings() {
    return this.settings;
  }

  get getNewsletterSnippets() {
    return this.snippetsCache;
  }

  get getNewsletterSnippetsTotal() {
    return this.snippetsTotal;
  }

  @Mutation
  [NewsletterMutation.SET_RENDER_UPDATE]() {
    this.renderKey++;
  }

  @Mutation
  [NewsletterMutation.SET_LIST_RESULT]({ listParams, listTotal, listCache }) {
    this.listParams = listParams;
    this.listCache = listCache;
    this.listTotal = listTotal;
  }

  @Mutation
  [NewsletterMutation.SET_SETTINGS_RESULT](settings) {
    this.settings = settings;
  }

  @Mutation
  [NewsletterMutation.SET_SNIPPETS_RESULT]({ list, total }) {
    this.snippetsCache = list;
    this.snippetsTotal = total;
  }

  @Action
  [NewsletterActions.FETCH_LIST](params) {
    return new Promise<void>((resolve, reject) => {
      apiService.post("newsletter/list", params).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        this.context.commit(NewsletterMutation.SET_LIST_RESULT, {
          listParams: params,
          listTotal: data.results,
          listCache: data.result
        });
        resolve();
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.REFRESH_LIST]() {
    return this.context.dispatch(NewsletterActions.FETCH_LIST, this.listParams);
  }

  @Action
  [NewsletterActions.GET_CAMPAIGN](id) {
    return new Promise((resolve, reject) => {
      apiService.get(`newsletter/get/${id}`).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.CREATE_CAMPAIGN](payload) {
    return new Promise((resolve, reject) => {
      apiService.post("newsletter/create", payload).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        this.context.commit(NewsletterMutation.SET_RENDER_UPDATE);
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.UPDATE_CAMPAIGN](payload) {
    return new Promise((resolve, reject) => {
      const { id, data: campaignData } = payload;
      apiService.put(`newsletter/update/${id}`, campaignData).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.DELETE_CAMPAIGN](id) {
    return new Promise((resolve, reject) => {
      apiService.delete(`newsletter/delete/${id}`).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        this.context.dispatch(NewsletterActions.REFRESH_LIST);
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.SCHEDULE_CAMPAIGN](payload) {
    return new Promise((resolve, reject) => {
      const { id, scheduledAt } = payload;
      apiService.post(`newsletter/schedule/${id}`, { scheduledAt } as any).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.CANCEL_CAMPAIGN](id) {
    return new Promise((resolve, reject) => {
      apiService.post(`newsletter/cancel/${id}`, {}).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        this.context.dispatch(NewsletterActions.REFRESH_LIST);
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.FETCH_STATS](id) {
    return new Promise((resolve, reject) => {
      apiService.get(`newsletter/stats/${id}`).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.AUDIENCE_PREVIEW](filter) {
    return new Promise((resolve, reject) => {
      apiService.post("newsletter/audience-preview", { filter } as any).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.COMPILE_CAMPAIGN](id) {
    return new Promise((resolve, reject) => {
      apiService.post(`newsletter/compile/${id}`, {}).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.PREVIEW_CAMPAIGN](id) {
    return new Promise((resolve, reject) => {
      apiService.get(`newsletter/preview/${id}`).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.SEND_TEST_MAIL](payload) {
    return new Promise((resolve, reject) => {
      const { id, emails } = payload;
      apiService.post(`newsletter/send-test/${id}`, { emails } as any).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.FETCH_AVAILABLE_BLOCKS](params) {
    return new Promise((resolve, reject) => {
      apiService.post("newsletter/available-blocks", params || {}).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.FETCH_AVAILABLE_BLOGS](params) {
    return new Promise((resolve, reject) => {
      apiService.post("newsletter/available-blogs", params || {}).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        resolve({ list: data.result, total: data.results });
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.FETCH_AVAILABLE_BENEFITS](params) {
    return new Promise((resolve, reject) => {
      apiService.post("newsletter/available-benefits", params || {}).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        resolve({ list: data.result, total: data.results });
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.FETCH_SETTINGS]() {
    return new Promise<void>((resolve, reject) => {
      apiService.get("newsletter/settings-get").then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        this.context.commit(NewsletterMutation.SET_SETTINGS_RESULT, data.result);
        resolve();
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.UPDATE_SETTINGS](payload) {
    return new Promise((resolve, reject) => {
      apiService.post("newsletter/settings-update", payload).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        this.context.dispatch(NewsletterActions.FETCH_SETTINGS);
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.TEST_CONNECTION](payload) {
    return new Promise((resolve, reject) => {
      apiService.post("newsletter/test-connection", payload || {}).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.FETCH_SNIPPETS](params) {
    return new Promise<void>((resolve, reject) => {
      apiService.post("newsletter/snippets-list", params || {}).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        this.context.commit(NewsletterMutation.SET_SNIPPETS_RESULT, {
          list: data.result,
          total: data.results
        });
        resolve();
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.GET_SNIPPET](id) {
    return new Promise((resolve, reject) => {
      apiService.get(`newsletter/snippets-get/${id}`).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.CREATE_SNIPPET](payload) {
    return new Promise((resolve, reject) => {
      apiService.post("newsletter/snippets-create", payload).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.UPDATE_SNIPPET](payload) {
    return new Promise((resolve, reject) => {
      const { id, data: snippetData } = payload;
      apiService.put(`newsletter/snippets-update/${id}`, snippetData).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }

  @Action
  [NewsletterActions.DELETE_SNIPPET](id) {
    return new Promise((resolve, reject) => {
      apiService.delete(`newsletter/snippets-delete/${id}`).then(({ data }) => {
        if (!apiService.verifyApiResult(data)) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }
        resolve(data.result);
      }).catch((response) => {
        this.context.commit(Mutations.SET_ERROR, response);
        reject();
      });
    });
  }
}
